import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import { Navigate, isRouteErrorResponse, useLocation, useNavigate, useRouteError } from "react-router";
import { Compass, Loader2, LogIn, ServerCrash, ShieldX, TriangleAlert } from "lucide-react";
import { isApiError, onSessionExpired, useSession, type TenantKind } from "@pmo/api-client";
import { useTranslation } from "@pmo/i18n";
import { BrandLogo, BrandMark, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErrorState } from "@pmo/ui";

export function FullPageLoader({ label }: { label?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className="grid min-h-dvh place-items-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-4 text-sm text-subtle">
        <BrandMark size={52} />
        <span className="inline-flex items-center gap-2">
          <Loader2 className="size-4 animate-spin text-aegold-700" aria-hidden />
          {label ?? t("states.loading")}
        </span>
      </div>
    </div>
  );
}

/** The API could not be reached: a friendly full-page state with retry (used before the shell can render). */
export function ApiUnavailable({ onRetry, retrying, fullPage = true }: { onRetry?: () => void; retrying?: boolean; fullPage?: boolean }) {
  const { t } = useTranslation("common");
  const body = <ErrorState title={t("errors.apiUnavailableTitle")} description={t("errors.apiUnavailableBody")} onRetry={onRetry} retrying={retrying} icon={ServerCrash} />;
  return fullPage ? (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="flex w-full max-w-lg flex-col items-center gap-6">
        <BrandLogo size={40} />
        <div className="w-full">{body}</div>
      </div>
    </div>
  ) : body;
}

/** Inline error for a failed query inside a page. */
export function QueryError({ error, onRetry, retrying }: { error: unknown; onRetry?: () => void; retrying?: boolean }) {
  const { t } = useTranslation("common");
  if (isApiError(error) && error.isUnavailable) return <ApiUnavailable fullPage={false} onRetry={onRetry} retrying={retrying} />;
  if (isApiError(error) && error.isForbidden) return <EmptyState icon={ShieldX} title={t("errors.forbiddenTitle")} description={t("errors.forbiddenBody")} />;
  return (
    <ErrorState
      title={t("errors.genericTitle")}
      description={isApiError(error) ? error.detail ?? t("errors.genericBody") : t("errors.genericBody")}
      detail={isApiError(error) && error.traceId ? t("errors.correlation", { id: error.traceId }) : undefined}
      onRetry={onRetry}
      retrying={retrying}
    />
  );
}

/**
 * Renders children for a signed-in user; otherwise redirects to the login page with the current address as `returnUrl`. Shows the
 * API-unavailable state when the session cannot be loaded. `kinds` limits the app to tenants of those kinds.
 */
export function RequireAuth({ children, loginPath = "/login", kinds, wrongKind }: { children: ReactNode; loginPath?: string; kinds?: TenantKind[]; wrongKind?: ReactNode }) {
  const { session, isLoading, error, refetch, isAuthenticated, tenant } = useSession();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);
  if (isLoading) return <FullPageLoader />;
  if (error && !session) {
    return (
      <ApiUnavailable
        retrying={retrying}
        onRetry={() => {
          setRetrying(true);
          void refetch().finally(() => setRetrying(false));
        }}
      />
    );
  }
  if (!isAuthenticated) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`${loginPath}?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  if (kinds && tenant && !kinds.includes(tenant.kind)) return <>{wrongKind ?? <ForbiddenPage />}</>;
  return <>{children}</>;
}

export function NotFoundPage({ homeHref = "/" }: { homeHref?: string }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-xl py-16">
      <EmptyState icon={Compass} title={t("errors.notFoundTitle")} description={t("errors.notFoundBody")} action={<Button onClick={() => navigate(homeHref)}>{t("actions.goHome")}</Button>} />
    </div>
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-xl py-16">
      <EmptyState icon={ShieldX} title={t("errors.forbiddenTitle")} description={t("errors.forbiddenBody")} action={<Button variant="outline" onClick={() => navigate("/")}>{t("actions.goHome")}</Button>} />
    </div>
  );
}

function BoundaryFallback({ error, onReset }: { error: unknown; onReset?: () => void }) {
  const { t } = useTranslation("common");
  const dev = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV;
  const detail = error instanceof Error && dev ? error.message : undefined;
  return (
    <div className="mx-auto max-w-xl py-16" data-screen-error>
      <ErrorState icon={TriangleAlert} title={t("errors.boundaryTitle")} description={t("errors.boundaryBody")} detail={detail} onRetry={onReset ?? (() => window.location.reload())}>
        <Button variant="ghost" onClick={() => window.location.assign("/")}>{t("actions.goHome")}</Button>
      </ErrorState>
    </div>
  );
}

/** Route `errorElement`: 404 responses render the not-found page, anything else the boundary fallback. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  return <BoundaryFallback error={error} />;
}

/** Catches render errors below it (a single page), keeping the shell usable. Resets when `resetKey` changes (the route). */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("[pmo] screen error", error, info.componentStack);
  }
  componentDidUpdate(prev: { resetKey?: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  render() {
    if (this.state.error) return <BoundaryFallback error={this.state.error} onReset={() => this.setState({ error: null })} />;
    return this.props.children;
  }
}

/** Opens when any request answers 401 while signed in; the user signs in again and returns to the same page. */
export function SessionExpiredDialog({ loginPath = "/login" }: { loginPath?: string }) {
  const { t } = useTranslation("common");
  const { isAuthenticated } = useSession();
  const [open, setOpen] = useState(false);
  useEffect(() => onSessionExpired(() => setOpen(true)), []);
  if (!isAuthenticated && !open) return null;
  const signIn = () => {
    const { pathname, search, hash } = window.location;
    window.location.assign(`${loginPath}?returnUrl=${encodeURIComponent(`${pathname}${search}${hash}`)}`);
  };
  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent size="sm" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("auth.sessionExpiredTitle")}</DialogTitle>
          <DialogDescription>{t("auth.sessionExpiredBody")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button primary onClick={signIn}>
            <LogIn aria-hidden />
            {t("auth.signInAgain")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
